/**
 * Базовый класс для работы с событиями
 * Реализует паттерн Observer для слабосвязанной архитектуры
 */
export class EventEmitter {
  constructor() {
    this.events = new Map();
  }

  /**
   * Подписка на событие
   * @param {string} event - Название события
   * @param {Function} listener - Обработчик события
   */
  on(event, listener) {
    if (!this.events.has(event)) {
      this.events.set(event, []);
    }
    this.events.get(event).push(listener);
  }

  /**
   * Одноразовая подписка на событие
   * @param {string} event - Название события
   * @param {Function} listener - Обработчик события
   */
  once(event, listener) {
    const onceListener = (...args) => {
      listener(...args);
      this.off(event, onceListener);
    };
    this.on(event, onceListener);
  }

  /**
   * Отписка от события
   * @param {string} event - Название события
   * @param {Function} listener - Обработчик события
   */
  off(event, listener) {
    if (!this.events.has(event)) return;

    const listeners = this.events.get(event);
    const index = listeners.indexOf(listener);

    if (index > -1) {
      listeners.splice(index, 1);
    }

    if (listeners.length === 0) {
      this.events.delete(event);
    }
  }

  /**
   * Генерация события
   * @param {string} event - Название события
   * @param {...any} args - Аргументы события
   */
  emit(event, ...args) {
    if (!this.events.has(event)) return;

    const listeners = this.events.get(event);
    listeners.forEach(listener => {
      try {
        listener(...args);
      } catch (error) {
        console.error(`Ошибка в обработчике события "${event}":`, error);
      }
    });
  }

  /**
   * Удаление всех подписчиков события
   * @param {string} event - Название события
   */
  removeAllListeners(event) {
    if (event) {
      this.events.delete(event);
    } else {
      this.events.clear();
    }
  }

  /**
   * Получение списка событий
   */
  getEvents() {
    return Array.from(this.events.keys());
  }

  /**
   * Получение количества подписчиков события
   * @param {string} event - Название события
   */
  listenerCount(event) {
    return this.events.has(event) ? this.events.get(event).length : 0;
  }
}
